import { useEffect, useState } from "react";

export function hubProductionStepTitles(engineTitles: string[] | undefined): string[] {
  if (!engineTitles?.length) return [];
  return engineTitles.map((title, index) => (index === 0 ? "Clock in (Hub labor)" : title));
}

export function useProductionStepConfirmReady(activeStepIndex: number, watchedStep: number, delayMs = 2000): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (activeStepIndex !== watchedStep) {
      setReady(false);
      return undefined;
    }
    setReady(false);
    const timerId = window.setTimeout(() => setReady(true), delayMs);
    return () => window.clearTimeout(timerId);
  }, [activeStepIndex, delayMs, watchedStep]);
  return ready;
}

export async function recordVisibleRackMcusForLaborSession(input: {
  sessionId: number;
  productionClient: import("@/lib/sdtestapp/sdTestAppProductionClient").SdTestAppProductionClient;
  recordedMcus: Set<string>;
}): Promise<void> {
  const visible = await input.productionClient.fetchVisibleLaborMcuUuids();
  const pending = visible.filter((mcuUuid) => !input.recordedMcus.has(mcuUuid));
  if (pending.length === 0) return;
  const { recordLaborMcuScans } = await import("@/lib/labor/laborApi");
  await recordLaborMcuScans({
    sessionId: input.sessionId,
    mcuUuids: pending,
    domain: "manufacturing",
  });
  for (const mcuUuid of pending) input.recordedMcus.add(mcuUuid);
}

export function downloadProductionReviewText(text: string, filename = "caltest-review-summary.txt"): void {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function formatProductionReviewResults(results: unknown[]): string {
  if (results.length === 0) return "No verification results published yet.";
  return results
    .map((entry, index) => {
      if (!entry || typeof entry !== "object") return `${index + 1}. ${String(entry)}`;
      const row = entry as Record<string, unknown>;
      const connector = String(row.connectorId ?? row.connector_id ?? "device");
      const status = String(row.status ?? row.result ?? row.outcome ?? "unknown");
      const detail = String(row.message ?? row.error ?? row.summary ?? "").trim();
      return detail ? `${connector}: ${status} — ${detail}` : `${connector}: ${status}`;
    })
    .join("\n");
}

export function formatSecondsEstimate(seconds?: number | null): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `~ ${hours} h ${minutes} min remaining`;
  if (minutes > 0) return `~ ${minutes} min ${secs} s remaining`;
  return `~ ${secs} s remaining`;
}
